import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

export type LegalDoc = 'privacy' | 'terms'

const CONTENT: Record<LegalDoc, { title: string; body: string }> = {
  privacy: {
    title: 'Privacy Policy',
    body: 'Trimmed Living LLC collects your name, email, phone number, and project details solely to communicate regarding your outdoor project and schedule consultations. If you provide a phone number, you agree to receive SMS communications regarding your inquiry (message & data rates may apply; reply STOP to opt out). We never sell, trade, or share your personal contact information with third parties. For questions, contact customercare@trimmedliving.com.',
  },
  terms: {
    title: 'Terms of Service',
    body: 'This website is provided for informational purposes by Trimmed Living LLC. Inquiries submitted do not constitute a binding agreement; all formal scopes of work and pricing require on-site consultation and a signed service agreement. All original media and content are property of Trimmed Living LLC. Links to our DIY shop at TrimmedLiving.com operate under their own separate terms.',
  },
}

export default function LegalModal({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const { title, body } = CONTENT[doc]

  useEffect(() => {
    closeRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button, a[href], [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-forest-950/70 p-4 backdrop-blur-sm sm:items-center"
      role="presentation"
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
        className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-bone-300 bg-bone-50 p-6 shadow-2xl sm:p-8"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-bone-300 text-forest-950/60 transition hover:bg-bone-200 hover:text-forest-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine"
        >
          <X className="h-4 w-4" />
        </button>

        <h2 id="legal-modal-title" className="pr-10 font-display text-2xl font-semibold text-forest-950">
          {title}
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-forest-950/70">{body}</p>
        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-950/35">
          Last updated 2026
        </p>
      </div>
    </div>
  )
}
